import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
} from '@mui/material';
import {
  Person,
  Psychology,
  Group,
} from '@mui/icons-material';
import { PatientAssessment } from '../../services/providerAssessmentService';

interface BiopsychosocialReviewProps {
  assessment: PatientAssessment;
}

const BiopsychosocialReview: React.FC<BiopsychosocialReviewProps> = ({ assessment }) => {
  const responses = assessment.responses;

  // Group responses by category based on question IDs
  const biologicalResponses = Object.entries(responses)
    .filter(([key]) => key.includes('biological') || key.includes('medical') || key.includes('health'))
    .slice(0, Math.ceil(Object.keys(responses).length / 3));

  const psychologicalResponses = Object.entries(responses)
    .filter(([key]) => key.includes('psychological') || key.includes('mental') || key.includes('mood'))
    .slice(0, Math.ceil(Object.keys(responses).length / 3));

  const socialResponses = Object.entries(responses)
    .filter(([key]) => key.includes('social') || key.includes('family') || key.includes('support'))
    .slice(0, Math.ceil(Object.keys(responses).length / 3));

  // If no categorized responses, divide equally
  const allEntries = Object.entries(responses);
  const perSection = Math.ceil(allEntries.length / 3);
  
  const biological = biologicalResponses.length > 0 ? biologicalResponses : allEntries.slice(0, perSection);
  const psychological = psychologicalResponses.length > 0 ? psychologicalResponses : allEntries.slice(perSection, perSection * 2);
  const social = socialResponses.length > 0 ? socialResponses : allEntries.slice(perSection * 2);

  return (
    <Box>
      {/* Assessment Overview */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50' }}>
        <Typography variant="h6" gutterBottom>
          Biopsychosocial Assessment Overview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive evaluation of biological, psychological, and social factors affecting the patient's condition.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Completed: {assessment.completedAt ? new Date(assessment.completedAt).toLocaleString() : 'In Progress'}
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Biological Factors */}
        <Grid item xs={12} lg={4}>
          <ResponseSection
            title="Biological Factors"
            subtitle="Physical health, medical history, and biological factors"
            icon={<Person sx={{ color: 'error.main' }} />}
            responses={biological}
            bgColor="error.50"
          />
        </Grid>

        {/* Psychological Factors */}
        <Grid item xs={12} lg={4}>
          <ResponseSection
            title="Psychological Factors"
            subtitle="Mental health, emotions, and psychological well-being"
            icon={<Psychology sx={{ color: 'warning.main' }} />}
            responses={psychological}
            bgColor="warning.50"
          />
        </Grid>

        {/* Social Factors */}
        <Grid item xs={12} lg={4}>
          <ResponseSection
            title="Social Factors"
            subtitle="Social support, relationships, and environmental factors"
            icon={<Group sx={{ color: 'info.main' }} />}
            responses={social}
            bgColor="info.50"
          />
        </Grid>
      </Grid>

      {/* Clinical Summary */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Clinical Summary
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body1" paragraph>
          <strong>Key Findings:</strong>
        </Typography>
        <Box sx={{ ml: 2 }}>
          <Typography variant="body2" paragraph>
            • Biological factors indicate {biological.length > 0 ? 'documented health concerns' : 'limited biological information provided'}
          </Typography>
          <Typography variant="body2" paragraph>
            • Psychological assessment reveals {psychological.length > 0 ? 'significant psychological factors' : 'minimal psychological information'}
          </Typography>
          <Typography variant="body2" paragraph>
            • Social support system shows {social.length > 0 ? 'identifiable social factors' : 'limited social context provided'}
          </Typography>
        </Box>
        
        <Typography variant="body1" sx={{ mt: 2 }}>
          <strong>Clinical Recommendations:</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mt: 1 }}>
          Based on the biopsychosocial assessment, consider the interaction of biological, psychological, 
          and social factors when determining FMLA eligibility and developing treatment recommendations.
        </Typography>
      </Paper>
    </Box>
  );
};

// Component for displaying responses in each category
interface ResponseSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  responses: [string, string][];
  bgColor: string;
}

const ResponseSection: React.FC<ResponseSectionProps> = ({
  title,
  subtitle,
  icon,
  responses,
  bgColor,
}) => (
  <Box sx={{ height: '100%' }}>
    {/* Section Header */}
    <Paper sx={{ p: 2, mb: 2, bgcolor: bgColor }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Paper>

    {/* Responses */}
    <Paper sx={{ p: 2, minHeight: 300 }}>
      {responses.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
          No responses in this category
        </Typography>
      ) : (
        <Box>
          {responses.map(([questionId, response], index) => (
            <Box key={questionId} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Response {index + 1}:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  bgcolor: 'grey.50', 
                  p: 1.5, 
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  minHeight: 40,
                }}
              >
                {response || 'No response provided'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  </Box>
);

export default BiopsychosocialReview;